import React, { useEffect, useState } from 'react';
import { useParams, useLocation, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { format } from 'date-fns';
import { formatTourId } from '../utils/tour';

const STATUS_BADGE = {
  draft: <span className="badge badge-draft">Draft</span>,
  submitted: <span className="badge badge-submitted">Pending Review</span>,
  acknowledged: <span className="badge badge-acknowledged">In Progress</span>,
  resolved: <span className="badge badge-resolved">Resolved</span>,
  escalated: <span className="badge badge-escalated">⚠ Escalated</span>,
};

const DELETE_ROLES = ['super_admin', 'regional_manager', 'executive_staff'];

export default function TourDetail() {
  const { id } = useParams();
  const { user, API } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('issues');
  const [issueUpdates, setIssueUpdates] = useState({});
  const [saving, setSaving] = useState({});
  // Trade (maintenance/environmental) per-issue action edits, keyed by issue id
  const [actionEdits, setActionEdits] = useState({});
  const [actionSaving, setActionSaving] = useState({});
  const [signingOff, setSigningOff] = useState(false);
  const justSubmitted = location.state?.justSubmitted;

  // Change-facility modal state
  const [facilityModalOpen, setFacilityModalOpen] = useState(false);
  const [facilities, setFacilities] = useState([]);
  const [facilitiesLoading, setFacilitiesLoading] = useState(false);
  const [targetFacilityId, setTargetFacilityId] = useState('');
  const [facilitySaving, setFacilitySaving] = useState(false);
  const [facilityError, setFacilityError] = useState('');

  const load = () => {
    API.get(`/tours/${id}`).then(r => {
      setData(r.data);
      const notes = {};
      r.data.issues.forEach(i => { notes[i.id] = { status: i.status, adminNotes: i.admin_notes || '' }; });
      setIssueUpdates(notes);
      // Pre-fill the current trade user's action edits from loaded actions
      if (['maintenance', 'environmental'].includes(user?.role)) {
        const edits = {};
        (r.data.issueActions || []).forEach(a => {
          if (a.role === user.role) edits[a.issue_id] = { status: a.status, comment: a.comment || '' };
        });
        setActionEdits(edits);
      }
    }).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [id]);

  const handleAcknowledge = async () => {
    await API.post(`/tours/${id}/acknowledge`);
    load();
  };

  const handleUnacknowledge = async () => {
    if (!window.confirm('Revert this tour to Pending Review? The reminder and escalation clock resumes from the original submission time, so an older tour may escalate on the next hourly check.')) return;
    try {
      await API.post(`/tours/${id}/unacknowledge`);
      load();
    } catch (e) {
      alert(e.response?.data?.error || 'Could not un-acknowledge this tour');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to permanently delete this tour? This cannot be undone.')) return;
    try {
      await API.delete(`/tours/${id}`);
      navigate('/tours');
    } catch (e) {
      alert('Failed to delete tour: ' + (e.response?.data?.error || e.message));
    }
  };

  const handleIssueUpdate = async (issueId, status, deferFields) => {
    setSaving(p => ({...p, [issueId]: true}));
    try {
      await API.put(`/tours/${id}/issues/${issueId}`, {
        status,
        adminNotes: issueUpdates[issueId]?.adminNotes || '',
        ...(deferFields || {})
      });
      setIssueUpdates(p => ({...p, [issueId]: {...p[issueId], status}}));
      setDeferOpen(p => ({...p, [issueId]: false}));
      load();
    } catch (e) {
      alert(e.response?.data?.error || 'Update failed');
    } finally {
      setSaving(p => ({...p, [issueId]: false}));
    }
  };

  // Defer form state: which issue's defer form is open + its fields
  const [deferOpen, setDeferOpen] = useState({});
  const [deferFields, setDeferFields] = useState({});

  // Save the current trade user's action (status + comment) on an issue
  const handleActionSave = async (issueId, status) => {
    setActionSaving(p => ({ ...p, [issueId]: true }));
    try {
      await API.put(`/tours/${id}/issues/${issueId}/action`, {
        status,
        comment: actionEdits[issueId]?.comment || '',
      });
      load();
    } catch (e) {
      alert('Failed to save: ' + (e.response?.data?.error || e.message));
    } finally {
      setActionSaving(p => ({ ...p, [issueId]: false }));
    }
  };

  // Tour-level sign-off for the current trade user's role
  const handleSignOff = async () => {
    const label = user?.role === 'maintenance' ? 'Maintenance' : 'Environmental';
    if (!window.confirm(`Sign off this tour as ${label}? This records your name and the current time and cannot be undone from the app.`)) return;
    setSigningOff(true);
    try {
      await API.post(`/tours/${id}/signoff`);
      load();
    } catch (e) {
      alert('Failed to sign off: ' + (e.response?.data?.error || e.message));
    } finally {
      setSigningOff(false);
    }
  };

  // Look up a trade action row for an issue+role from the loaded data
  const getAction = (issueId, role) =>
    (data?.issueActions || []).find(a => a.issue_id === issueId && a.role === role);

  // Authenticated per-tour report download (PDF or Excel)
  const [exporting, setExporting] = useState('');
  const downloadReport = async (fmt) => {
    setExporting(fmt);
    try {
      const r = await API.get(`/reports/tour/${id}/detail?format=${fmt}`, { responseType: 'blob' });
      const disposition = r.headers['content-disposition'] || '';
      const match = disposition.match(/filename="([^"]+)"/);
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = match ? match[1] : `pillar-tour.${fmt}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      alert('Export failed: ' + (e.response?.data?.error || e.message));
    } finally {
      setExporting('');
    }
  };

  const openFacilityModal = async () => {
    setFacilityModalOpen(true);
    setFacilityError('');
    setTargetFacilityId('');
    if (facilities.length === 0) {
      setFacilitiesLoading(true);
      try {
        const r = await API.get('/admin/facilities?tourable=true');
        setFacilities(r.data);
      } catch (e) {
        setFacilityError('Failed to load facilities.');
      } finally {
        setFacilitiesLoading(false);
      }
    }
  };

  const handleChangeFacility = async () => {
    if (!targetFacilityId) {
      setFacilityError('Please select a facility.');
      return;
    }
    setFacilitySaving(true);
    setFacilityError('');
    try {
      await API.put(`/tours/${id}/facility`, { facilityId: targetFacilityId });
      setFacilityModalOpen(false);
      load();
    } catch (e) {
      setFacilityError(e.response?.data?.error || 'Failed to change facility.');
    } finally {
      setFacilitySaving(false);
    }
  };

  if (loading) return <div className="loading-screen"><div className="spinner"/></div>;
  if (!data) return <div className="page-body"><p>Tour not found.</p></div>;

  const { tour, responses, issues } = data;
  const score = parseFloat(tour.compliance_score);
  const scoreClass = score >= 90 ? 'score-high' : score >= 75 ? 'score-mid' : 'score-low';
  const isAdmin = ['building_admin', 'regional_manager', 'executive_staff', 'super_admin'].includes(user?.role);
  const isTrade = ['maintenance', 'environmental'].includes(user?.role);
  const myTradeSignedOff = isTrade && (
    user?.role === 'maintenance' ? tour.maintenance_signed_off_at : tour.environmental_signed_off_at
  );
  const isDraft = tour.status === 'draft';
  const isOwnDraft = isDraft && tour.conducted_by === user?.id;
  // Backend allows: elevated roles on any tour, or the creator on their own draft.
  const canDelete = DELETE_ROLES.includes(user?.role) || isOwnDraft;
  const canChangeFacility = user?.role === 'super_admin' && tour.status !== 'draft';
  const openIssues = issues.filter(i => i.status !== 'resolved');
  const resolvedIssues = issues.filter(i => i.status === 'resolved');

  // Group responses by category
  const byCategory = {};
  responses.forEach(r => {
    if (!byCategory[r.category_name]) byCategory[r.category_name] = [];
    byCategory[r.category_name].push(r);
  });

  return (
    <>
      <div className="page-header">
        <div>
          <div style={{fontSize:12,color:'var(--text-secondary)',marginBottom:4}}>
            <Link to="/tours" style={{color:'var(--blue)',textDecoration:'none'}}>Tours</Link> → {tour.facility_name}
          </div>
          <div style={{display:'flex',alignItems:'center',gap:10,flexWrap:'wrap'}}>
            <h2>{tour.facility_name} — Facility Tour</h2>
            <span style={{
              fontFamily:'DM Mono, monospace',
              fontSize:13, fontWeight:600,
              padding:'3px 10px', borderRadius:6,
              background:'var(--gray-100)', color:'var(--gray-700)',
              border:'1px solid var(--border)'
            }}>{formatTourId(tour.tour_number)}</span>
          </div>
          <p>
            Conducted by {tour.staff_name} · {tour.submitted_at ? format(new Date(tour.submitted_at), 'MMMM d, yyyy h:mm a') : 'Draft'}
          </p>
        </div>
        <div style={{display:'flex',gap:12,alignItems:'center',flexWrap:'wrap'}}>
          {STATUS_BADGE[tour.status]}
          {isAdmin && tour.status !== 'draft' && (
            <>
              <button className="btn btn-secondary btn-sm" onClick={() => downloadReport('pdf')} disabled={!!exporting}>
                {exporting === 'pdf' ? 'Exporting...' : '⬇ PDF Report'}
              </button>
              <button className="btn btn-secondary btn-sm" onClick={() => downloadReport('xlsx')} disabled={!!exporting}>
                {exporting === 'xlsx' ? 'Exporting...' : '⬇ Excel'}
              </button>
            </>
          )}
          {/* Trade sign-off chips (visible to everyone once signed) */}
          {tour.maintenance_signed_off_at && (
            <span className="badge badge-resolved" title={`Signed off ${format(new Date(tour.maintenance_signed_off_at), 'MMM d, yyyy h:mm a')}`}>
              🔧 Maint: {tour.maintenance_signed_off_by_name || 'signed'}
            </span>
          )}
          {tour.environmental_signed_off_at && (
            <span className="badge badge-resolved" title={`Signed off ${format(new Date(tour.environmental_signed_off_at), 'MMM d, yyyy h:mm a')}`}>
              🧹 Environ: {tour.environmental_signed_off_by_name || 'signed'}
            </span>
          )}
          {isTrade && !myTradeSignedOff && tour.status !== 'draft' && (
            <button className="btn btn-success" onClick={handleSignOff} disabled={signingOff}>
              {signingOff ? 'Signing...' : `✓ Sign Off (${user.role === 'maintenance' ? 'Maintenance' : 'Environmental'})`}
            </button>
          )}
          {isOwnDraft && (
            <Link to={`/tours/${tour.id}/edit`} className="btn btn-primary">Resume Editing</Link>
          )}
          {['building_admin', 'super_admin'].includes(user?.role) && tour.status === 'submitted' && (
            <button className="btn btn-primary" onClick={handleAcknowledge}>Acknowledge Tour</button>
          )}
          {['regional_manager', 'executive_staff'].includes(user?.role) && tour.status === 'submitted' && (
            <span style={{ fontSize: 12, color: 'var(--text-secondary)', fontStyle: 'italic' }}>
              Awaiting acknowledgment by the building admin — acknowledging is a building-level action that marks this tour as being worked.
            </span>
          )}
          {user?.role === 'super_admin' && tour.status === 'acknowledged' && (
            <button className="btn btn-secondary btn-sm" onClick={handleUnacknowledge}>Un-acknowledge</button>
          )}
          {canChangeFacility && (
            <button className="btn btn-secondary btn-sm" onClick={openFacilityModal}>
              Change Facility
            </button>
          )}
          {canDelete && (
            <button
              className="btn btn-sm"
              style={{background:'#FCEBEB',color:'#A32D2D',border:'1px solid #F09595'}}
              onClick={handleDelete}
            >
              🗑 Delete Tour
            </button>
          )}
        </div>
      </div>

      <div className="page-body">
        {justSubmitted && (
          <div className="alert-banner alert-success" style={{marginBottom:20}}>
            ✅ Tour submitted successfully! The building administrator has been notified by email.
          </div>
        )}

        {tour.status === 'escalated' && (
          <div className="alert-banner alert-danger" style={{marginBottom:20}}>
            🚨 This tour has been escalated to regional management. Admin did not respond within 48 hours.
          </div>
        )}

        {/* Summary Row */}
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(200px,1fr))',gap:16,marginBottom:24}}>
          <div className="card" style={{textAlign:'center'}}>
            <div className="stat-label">Compliance Score</div>
            <div style={{margin:'12px auto'}}>
              <span className={`score-circle ${scoreClass}`} style={{width:72,height:72,fontSize:20}}>
                {score.toFixed(0)}%
              </span>
            </div>
            <div style={{fontSize:13,color:'var(--text-secondary)'}}>
              {tour.compliant_count} / {tour.total_questions} compliant
            </div>
          </div>
          <div className="card" style={{textAlign:'center'}}>
            <div className="stat-label">Open Issues</div>
            <div className="stat-value" style={{color: openIssues.length > 0 ? 'var(--red)' : 'var(--green)'}}>{openIssues.length}</div>
            <div style={{fontSize:13,color:'var(--text-secondary)'}}>require attention</div>
          </div>
          <div className="card" style={{textAlign:'center'}}>
            <div className="stat-label">Resolved</div>
            <div className="stat-value" style={{color:'var(--green)'}}>{resolvedIssues.length}</div>
            <div style={{fontSize:13,color:'var(--text-secondary)'}}>issues closed</div>
          </div>
          {tour.submitted_at && (
            <div className="card" style={{textAlign:'center'}}>
              <div className="stat-label">Time Since Submission</div>
              {(() => {
                const hrs = Math.floor((Date.now() - new Date(tour.submitted_at)) / 3600000);
                const color = hrs >= 48 ? 'var(--red)' : hrs >= 24 ? 'var(--amber)' : 'var(--gray-700)';
                return (
                  <>
                    <div className="stat-value" style={{color}}>{hrs}h</div>
                    <div style={{fontSize:13,color:'var(--text-secondary)'}}>
                      {hrs < 48 ? `${48 - hrs}h until escalation` : 'escalation threshold passed'}
                    </div>
                  </>
                );
              })()}
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="tabs">
          <button className={`tab-btn ${activeTab === 'issues' ? 'active' : ''}`} onClick={() => setActiveTab('issues')}>
            Issues {openIssues.length > 0 && <span style={{background:'var(--red)',color:'white',borderRadius:'999px',padding:'1px 7px',fontSize:11,marginLeft:6}}>{openIssues.length}</span>}
          </button>
          <button className={`tab-btn ${activeTab === 'responses' ? 'active' : ''}`} onClick={() => setActiveTab('responses')}>
            All Responses
          </button>
        </div>

        {/* Issues Tab */}
        {activeTab === 'issues' && (
          <div>
            {issues.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">🎉</div>
                <h3>No Issues Found</h3>
                <p>All items were marked compliant in this tour.</p>
              </div>
            ) : (
              <>
                {openIssues.length > 0 && (
                  <div style={{marginBottom:24}}>
                    <h3 style={{fontSize:15,fontWeight:700,color:'var(--text-heading)',marginBottom:12}}>
                      Open Issues ({openIssues.length})
                    </h3>
                    {openIssues.map(issue => (
                      <div key={issue.id} className="issue-item">
                        <div className="issue-header">
                          <div>
                            <div className="issue-category">{issue.category_name}</div>
                            <div className="issue-text">{issue.question_text}</div>
                            {issue.notes && <div className="issue-notes">Staff note: "{issue.notes}"</div>}
                          </div>
                          <span className={`badge badge-${issue.status.replace('_','-')}`}>{issue.status.replace('_',' ')}</span>
                        </div>
                        {isAdmin && (
                          <div style={{marginTop:10}}>
                            <textarea
                              className="admin-notes-input"
                              placeholder="Add admin notes / corrective action taken..."
                              value={issueUpdates[issue.id]?.adminNotes || ''}
                              onChange={e => setIssueUpdates(p => ({
                                ...p,
                                [issue.id]: {...p[issue.id], adminNotes: e.target.value}
                              }))}
                            />
                            <div className="issue-actions" style={{marginTop:8}}>
                              <button
                                className="btn btn-secondary btn-sm"
                                onClick={() => handleIssueUpdate(issue.id, 'in_progress')}
                                disabled={saving[issue.id] || issue.status === 'in_progress'}
                              >Mark In Progress</button>
                              <button
                                className="btn btn-success btn-sm"
                                onClick={() => handleIssueUpdate(issue.id, 'resolved')}
                                disabled={saving[issue.id]}
                              >✓ Mark Resolved</button>
                              {issue.status !== 'deferred' && (
                                <button
                                  className="btn btn-secondary btn-sm"
                                  onClick={() => setDeferOpen(p => ({...p, [issue.id]: !p[issue.id]}))}
                                  disabled={saving[issue.id]}
                                >⏸ Defer (long-term)</button>
                              )}
                            </div>
                            {deferOpen[issue.id] && (
                              <div style={{marginTop:10, padding:12, border:'1px solid var(--amber)', borderRadius:6, background:'rgba(245,158,11,0.06)'}}>
                                <div style={{fontSize:12, fontWeight:700, color:'var(--amber)', marginBottom:8}}>
                                  Defer this issue — for items that can't be completed in normal timeframes (CAPEX approvals, contractor bids). It will stop counting as open, the tour can resolve without it, and future tours flagging the same item will inherit this deferral automatically.
                                </div>
                                <input
                                  className="form-control"
                                  placeholder="Reason (required) — e.g. Awaiting CAPEX approval for parking lot repair"
                                  value={deferFields[issue.id]?.reason || ''}
                                  onChange={e => setDeferFields(p => ({...p, [issue.id]: {...p[issue.id], reason: e.target.value}}))}
                                  style={{marginBottom:8}}
                                />
                                <div style={{display:'flex', gap:8, alignItems:'center', flexWrap:'wrap'}}>
                                  <label style={{fontSize:12, color:'var(--text-secondary)'}}>Target / review date:</label>
                                  <input
                                    type="date"
                                    className="form-control"
                                    style={{width:'auto'}}
                                    value={deferFields[issue.id]?.until || ''}
                                    onChange={e => setDeferFields(p => ({...p, [issue.id]: {...p[issue.id], until: e.target.value}}))}
                                  />
                                  <button
                                    className="btn btn-primary btn-sm"
                                    disabled={saving[issue.id] || !(deferFields[issue.id]?.reason || '').trim() || !deferFields[issue.id]?.until}
                                    onClick={() => handleIssueUpdate(issue.id, 'deferred', {
                                      deferredReason: deferFields[issue.id]?.reason,
                                      deferredUntil: deferFields[issue.id]?.until,
                                    })}
                                  >Confirm Defer</button>
                                </div>
                              </div>
                            )}
                            {issue.status === 'deferred' && (
                              <div style={{marginTop:10, padding:10, borderLeft:'3px solid var(--amber)', background:'rgba(245,158,11,0.06)', fontSize:12.5}}>
                                <span style={{fontWeight:700, color:'var(--amber)'}}>⏸ Deferred</span>
                                {issue.deferred_reason && <> — {issue.deferred_reason}</>}
                                {issue.deferred_until && <> · target {new Date(issue.deferred_until).toLocaleDateString()}{new Date(issue.deferred_until) < new Date() && <span style={{color:'var(--red)', fontWeight:700}}> (OVERDUE)</span>}</>}
                                {issue.carried_from_issue_id && <span style={{color:'var(--text-secondary)'}}> · carried forward from a previous tour</span>}
                                <div style={{marginTop:6}}>
                                  <button className="btn btn-secondary btn-sm" onClick={() => handleIssueUpdate(issue.id, 'in_progress')} disabled={saving[issue.id]}>Reactivate</button>
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Trade work trail — visible to admins & trade staff */}
                        {(isAdmin || isTrade) && (
                          <div style={{marginTop:10,borderTop:'1px dashed var(--border)',paddingTop:10}}>
                            {['maintenance', 'environmental'].map(role => {
                              const action = getAction(issue.id, role);
                              const roleLabel = role === 'maintenance' ? '🔧 Maintenance' : '🧹 Environmental';
                              const isMine = isTrade && user.role === role;
                              const statusColors = { complete: 'var(--green)', in_progress: 'var(--amber)', open: 'var(--text-secondary)' };

                              if (isMine) {
                                // Editable block for the current trade user's own role
                                return (
                                  <div key={role} style={{marginBottom:10}}>
                                    <div style={{fontSize:12,fontWeight:700,color:'var(--gray-700)',marginBottom:6}}>{roleLabel} — your action</div>
                                    <textarea
                                      className="admin-notes-input"
                                      placeholder="Describe work performed / findings..."
                                      value={actionEdits[issue.id]?.comment ?? action?.comment ?? ''}
                                      onChange={e => setActionEdits(p => ({
                                        ...p, [issue.id]: { ...p[issue.id], comment: e.target.value }
                                      }))}
                                    />
                                    <div className="issue-actions" style={{marginTop:6}}>
                                      <button
                                        className="btn btn-secondary btn-sm"
                                        onClick={() => handleActionSave(issue.id, 'in_progress')}
                                        disabled={actionSaving[issue.id]}
                                      >Mark In Progress</button>
                                      <button
                                        className="btn btn-success btn-sm"
                                        onClick={() => handleActionSave(issue.id, 'complete')}
                                        disabled={actionSaving[issue.id]}
                                      >{actionSaving[issue.id] ? 'Saving...' : '✓ Mark My Part Complete'}</button>
                                    </div>
                                  </div>
                                );
                              }

                              // Read-only display of the other role's (or for admins, both roles') state
                              if (!action) {
                                return (
                                  <div key={role} style={{fontSize:12,color:'var(--text-secondary)',marginBottom:4}}>
                                    {roleLabel}: <span style={{fontStyle:'italic'}}>no action recorded</span>
                                  </div>
                                );
                              }
                              return (
                                <div key={role} style={{fontSize:12,marginBottom:6}}>
                                  <span style={{fontWeight:700,color:'var(--gray-700)'}}>{roleLabel}:</span>{' '}
                                  <span style={{color:statusColors[action.status],fontWeight:600,textTransform:'capitalize'}}>{action.status.replace('_',' ')}</span>
                                  {action.completed_by_name && action.completed_at && (
                                    <span style={{color:'var(--text-secondary)'}}> — {action.completed_by_name}, {format(new Date(action.completed_at), 'MMM d h:mm a')}</span>
                                  )}
                                  {action.comment && (
                                    <div style={{color:'var(--text-secondary)',fontStyle:'italic',marginTop:2}}>"{action.comment}"</div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {resolvedIssues.length > 0 && (
                  <div>
                    <h3 style={{fontSize:15,fontWeight:700,color:'var(--text-secondary)',marginBottom:12}}>
                      Resolved ({resolvedIssues.length})
                    </h3>
                    {resolvedIssues.map(issue => (
                      <div key={issue.id} className="issue-item resolved">
                        <div className="issue-header">
                          <div>
                            <div className="issue-category">{issue.category_name}</div>
                            <div className="issue-text">{issue.question_text}</div>
                            {issue.admin_notes && (
                              <div style={{fontSize:13,color:'var(--green)',marginTop:4}}>✓ {issue.admin_notes}</div>
                            )}
                          </div>
                          <div style={{display:'flex',alignItems:'center',gap:8}}>
                            <span className="badge badge-resolved">Resolved</span>
                            {isAdmin && (
                              <button
                                className="btn btn-secondary btn-sm"
                                onClick={() => handleIssueUpdate(issue.id, 'open')}
                                disabled={saving[issue.id]}
                              >Unresolve</button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* All Responses Tab */}
        {activeTab === 'responses' && (
          <div>
            {Object.entries(byCategory).map(([cat, catResponses]) => (
              <div key={cat} className="card" style={{marginBottom:16}}>
                <h3 className="card-title">{cat}</h3>
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Question</th>
                        <th>Response</th>
                        <th>Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {catResponses.map(r => (
                        <tr key={r.id}>
                          <td style={{color:'var(--text-secondary)',fontSize:12}}>{r.question_number}</td>
                          <td style={{maxWidth:400}}>{r.question_text}</td>
                          <td>
                            {r.is_compliant
                              ? <span className="badge badge-compliant">✓ Compliant</span>
                              : r.response_value !== null
                              ? <span className="badge badge-not-compliant">✗ Non-Compliant</span>
                              : <span style={{color:'var(--text-secondary)',fontSize:12}}>Not answered</span>
                            }
                          </td>
                          <td style={{color:'var(--text-secondary)',fontSize:13}}>{r.notes || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Change Facility Modal */}
      {facilityModalOpen && (
        <div className="modal-overlay" onClick={() => !facilitySaving && setFacilityModalOpen(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 480 }}>
            <div className="modal-header">
              <h3>Change Facility</h3>
              <button className="modal-close" onClick={() => !facilitySaving && setFacilityModalOpen(false)}>×</button>
            </div>
            <div className="modal-body">
              <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 12 }}>
                Reassign this tour from <strong style={{color:'var(--text-primary)'}}>{tour.facility_name}</strong> to another facility.
                Responses, issues, and compliance score remain unchanged.
              </p>
              <div className="form-group" style={{marginBottom: 0}}>
                <label className="form-label">New Facility</label>
                {facilitiesLoading ? (
                  <div style={{fontSize:13,color:'var(--text-secondary)'}}>Loading facilities...</div>
                ) : (
                  <select
                    className="form-control"
                    value={targetFacilityId}
                    onChange={e => { setTargetFacilityId(e.target.value); setFacilityError(''); }}
                    disabled={facilitySaving}
                  >
                    <option value="">-- Select Facility --</option>
                    {facilities
                      .filter(f => f.id !== tour.facility_id)
                      .map(f => <option key={f.id} value={f.id}>{f.name}</option>)
                    }
                  </select>
                )}
                {facilityError && <div className="field-error">{facilityError}</div>}
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setFacilityModalOpen(false)}
                disabled={facilitySaving}
              >Cancel</button>
              <button
                className="btn btn-primary"
                onClick={handleChangeFacility}
                disabled={facilitySaving || facilitiesLoading || !targetFacilityId}
              >{facilitySaving ? 'Saving...' : 'Reassign Tour'}</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
